import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import axios from "axios";
import { CheckCircle, ArrowLeft, Eye, EyeOff, Info, Copy } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/* Auto-formats raw digits (max 9) into 123-456-789 */
function formatTrn(raw) {
  const d = raw.replace(/\D/g, "").slice(0, 9);
  if (d.length <= 3) return d;
  if (d.length <= 6) return `${d.slice(0, 3)}-${d.slice(3)}`;
  return `${d.slice(0, 3)}-${d.slice(3, 6)}-${d.slice(6)}`;
}


const COUNTRIES = [
  "Jamaica",
  "Trinidad and Tobago",
  "Barbados",
  "The Bahamas",
  "Saint Lucia",
  "Grenada",
  "Saint Vincent and the Grenadines",
  "Antigua and Barbuda",
  "Dominica",
  "Saint Kitts and Nevis",
  "Guyana",
  "Belize",
  "Suriname",
  "Haiti",
  "Dominican Republic",
  "Cuba",
  "Cayman Islands",
  "United States",
  "Canada",
  "United Kingdom",
  "Germany",
  "France",
  "Netherlands",
  "Spain",
  "Italy",
  "Portugal",
  "Brazil",
  "Mexico",
  "Colombia",
  "Venezuela",
  "Panama",
  "Costa Rica",
  "Honduras",
  "Guatemala",
  "El Salvador",
  "China",
  "Japan",
  "Australia",
  "New Zealand",
  "India",
  "Singapore",
  "South Africa",
  "Nigeria",
  "Ghana",
  "Kenya",
  "Other",
];

const JA_PARISHES = [
  "Kingston", "St. Andrew", "St. Thomas", "Portland", "St. Mary",
  "St. Ann", "Trelawny", "St. James", "Hanover", "Westmoreland",
  "St. Elizabeth", "Manchester", "Clarendon", "St. Catherine",
];

/* Display name → ISO 3166-1 alpha-2 country code */
const COUNTRY_ISO = {
  "Jamaica": "JM",
  "Trinidad and Tobago": "TT",
  "Barbados": "BB",
  "The Bahamas": "BS",
  "Saint Lucia": "LC",
  "Grenada": "GD",
  "Saint Vincent and the Grenadines": "VC",
  "Antigua and Barbuda": "AG",
  "Dominica": "DM",
  "Saint Kitts and Nevis": "KN",
  "Guyana": "GY",
  "Belize": "BZ",
  "Suriname": "SR",
  "Haiti": "HT",
  "Dominican Republic": "DO",
  "Cuba": "CU",
  "Cayman Islands": "KY",
  "United States": "US",
  "Canada": "CA",
  "United Kingdom": "GB",
  "Germany": "DE",
  "France": "FR",
  "Netherlands": "NL",
  "Spain": "ES",
  "Italy": "IT",
  "Portugal": "PT",
  "Brazil": "BR",
  "Mexico": "MX",
  "Colombia": "CO",
  "Venezuela": "VE",
  "Panama": "PA",
  "Costa Rica": "CR",
  "Honduras": "HN",
  "Guatemala": "GT",
  "El Salvador": "SV",
  "China": "CN",
  "Japan": "JP",
  "Australia": "AU",
  "New Zealand": "NZ",
  "India": "IN",
  "Singapore": "SG",
  "South Africa": "ZA",
  "Nigeria": "NG",
  "Ghana": "GH",
  "Kenya": "KE",
};

const ERROR_MAP = {
  402: "This account is locked. Please contact KYo support.",
  409: "An account with this email already exists. Please sign in instead.",
  422: "Invalid submission format. Please check your details and try again.",
};

/* Returns true if the API error signals a duplicate/already-registered email */
function isDuplicateEmail(err) {
  const status = err?.response?.status;
  if (status === 409) return true;
  const body = JSON.stringify(err?.response?.data || "").toLowerCase();
  return (
    body.includes("email") &&
    (body.includes("already") || body.includes("exist") || body.includes("duplicate") || body.includes("registered") || body.includes("taken"))
  );
}

function getApiError(err) {
  const status = err?.response?.status;
  const detail = err?.response?.data?.detail;
  /* Prefer the upstream detail — it's more specific than our generic map */
  return detail || ERROR_MAP[status] || "Registration failed. Please try again.";
}

/* ── Floating-label text input ── */
function FloatInput({ label, id, type = "text", value, onChange, required, placeholder, className = "" }) {
  return (
    <div className={`relative ${className}`}>
      <label
        htmlFor={id}
        className="absolute -top-2.5 left-3 z-10 bg-white px-1 text-xs text-gray-500 leading-none select-none"
      >
        {label}
        {required && <span className="text-[#ED2E24] ml-0.5">*</span>}
      </label>
      <input
        id={id}
        type={type}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        className="w-full border border-gray-300 rounded-xl px-3.5 pt-4 pb-3 text-sm text-gray-900 bg-white outline-none focus:border-gray-500 transition-colors placeholder:text-gray-300"
      />
    </div>
  );
}

/* ── Floating-label password input with show/hide toggle ── */
function PasswordInput({ label, id, value, onChange, required, className = "" }) {
  const [show, setShow] = useState(false);
  return (
    <div className={`relative ${className}`}>
      <label
        htmlFor={id}
        className="absolute -top-2.5 left-3 z-10 bg-white px-1 text-xs text-gray-500 leading-none select-none"
      >
        {label}
        {required && <span className="text-[#ED2E24] ml-0.5">*</span>}
      </label>
      <input
        id={id}
        type={show ? "text" : "password"}
        value={value}
        onChange={onChange}
        className="w-full border border-gray-300 rounded-xl px-3.5 pt-4 pb-3 pr-10 text-sm text-gray-900 bg-white outline-none focus:border-gray-500 transition-colors"
      />
      <button
        type="button"
        onClick={() => setShow((s) => !s)}
        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
        tabIndex={-1}
      >
        {show ? <EyeOff size={15} /> : <Eye size={15} />}
      </button>
    </div>
  );
}

/* ── Floating-label select ── */
function FloatSelect({ label, id, value, onChange, options, required, className = "" }) {
  return (
    <div className={`relative ${className}`}>
      <label
        htmlFor={id}
        className="absolute -top-2.5 left-3 z-10 bg-white px-1 text-xs text-gray-500 leading-none select-none"
      >
        {label}
        {required && <span className="text-[#ED2E24] ml-0.5">*</span>}
      </label>
      <select
        id={id}
        value={value}
        onChange={onChange}
        className="w-full border border-gray-300 rounded-xl px-3.5 pt-4 pb-3 pr-8 text-sm text-gray-900 bg-white outline-none focus:border-gray-500 transition-colors appearance-none cursor-pointer"
      >
        {options.map((o) => (
          <option key={o} value={o}>
            {o}
          </option>
        ))}
      </select>
      <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-gray-400">
        <svg width="12" height="8" viewBox="0 0 12 8" fill="none">
          <path d="M1 1.5l5 5 5-5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </span>
    </div>
  );
}

/* ── Google Places address autocomplete ── */
const GMAPS_KEY = process.env.REACT_APP_GOOGLE_PLACES_API_KEY;
let _gmapsPromise = null;

function loadGoogleMaps() {
  if (window.google?.maps?.places) return Promise.resolve();
  if (_gmapsPromise) return _gmapsPromise;
  _gmapsPromise = new Promise((resolve, reject) => {
    window.__kyoMapsReady = resolve;
    const s = document.createElement("script");
    s.src = `https://maps.googleapis.com/maps/api/js?key=${GMAPS_KEY}&v=weekly&libraries=places&callback=__kyoMapsReady`;
    s.async = true;
    s.onerror = () => { _gmapsPromise = null; reject(); };
    document.head.appendChild(s);
  });
  return _gmapsPromise;
}

const AddressAutocomplete = React.forwardRef(function AddressAutocomplete(
  { label, id, value, onChange, required, placeholder, countryCode, className = "" },
  forwardedRef
) {
  const inputRef = useRef(null);
  const acRef = useRef(null);

  /* Combine internal ref with forwarded ref so parent can read DOM value at submit time */
  const setRefs = (el) => {
    inputRef.current = el;
    if (typeof forwardedRef === "function") forwardedRef(el);
    else if (forwardedRef) forwardedRef.current = el;
  };

  /* Keep DOM in sync when parent value changes (e.g. after place selected) */
  useEffect(() => {
    if (inputRef.current && document.activeElement !== inputRef.current) {
      inputRef.current.value = value;
    }
  }, [value]);

  useEffect(() => {
    let alive = true;
    loadGoogleMaps()
      .then(() => {
        if (!alive || !inputRef.current || acRef.current) return;
        const options = {
          types: ["address"],
          fields: ["formatted_address", "address_components", "name"],
          ...(countryCode && { componentRestrictions: { country: countryCode } }),
        };
        acRef.current = new window.google.maps.places.Autocomplete(inputRef.current, options);
        acRef.current.addListener("place_changed", () => {
          const place = acRef.current.getPlace();

          /* 1. Reconstruct from granular address_components — most reliable on mobile */
          let addr = "";
          if (place?.address_components?.length) {
            const get = (type) =>
              place.address_components.find((c) => c.types.includes(type))?.long_name || "";
            const streetNum = get("street_number");
            const route     = get("route");
            const locality  = get("locality") || get("administrative_area_level_2");
            const country   = get("country");
            const street    = streetNum && route ? `${streetNum} ${route}` : route || streetNum;
            addr = [street, locality, country].filter(Boolean).join(", ");
          }

          /* 2. Fall back to formatted_address */
          if (!addr) addr = place?.formatted_address || "";

          /* 3. Last resort: whatever text is currently in the input (mobile fills it before firing place_changed) */
          if (!addr) addr = inputRef.current?.value || "";

          /* Always call onChange — if Places gave nothing, fall back to whatever the user typed */
          const finalAddr = addr || inputRef.current?.value || "";
          if (inputRef.current) inputRef.current.value = finalAddr;
          onChange(finalAddr);
        });
      })
      .catch(() => {/* fall back to plain text input silently */});
    return () => { alive = false; };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className={`relative ${className}`}>
      <label
        htmlFor={id}
        className="absolute -top-2.5 left-3 z-10 bg-white px-1 text-xs text-gray-500 leading-none select-none"
      >
        {label}
        {required && <span className="text-[#ED2E24] ml-0.5">*</span>}
      </label>
      <input
        ref={setRefs}
        id={id}
        type="text"
        defaultValue={value}
        onChange={(e) => onChange(e.target.value)}
        onBlur={(e) => { const v = e.target.value.trim(); if (v) onChange(v); }}
        placeholder={placeholder}
        autoComplete="off"
        className="w-full border border-gray-300 rounded-xl px-3.5 pt-4 pb-3 text-sm text-gray-900 bg-white outline-none focus:border-gray-500 transition-colors placeholder:text-gray-300"
        data-testid={id}
      />
    </div>
  );
});

/* ── Success screen ── */
function SuccessScreen({ customerNumber, onBack }) {
  const copy = () => {
    navigator.clipboard.writeText(customerNumber);
    toast.success("Customer ID copied to clipboard!");
  };

  return (
    <div className="text-center py-4" data-testid="signup-success-screen">
      <div className="flex justify-center mb-5">
        <div className="w-20 h-20 rounded-full bg-green-50 flex items-center justify-center">
          <CheckCircle size={48} className="text-green-500" />
        </div>
      </div>
      <h2 className="text-2xl font-bold text-gray-900 mb-2">Registration Successful!</h2>
      <p className="text-gray-500 text-sm mb-8 leading-relaxed">
        Welcome to KYo Group. Your account has been created and is now visible in the KYo admin dashboard for courier admins and dispatch.
      </p>

      <div className="bg-gray-50 border border-gray-100 rounded-2xl p-6 mb-6">
        <p className="text-xs font-medium text-gray-400 uppercase tracking-wider mb-3">Your KYo Customer ID</p>
        <div className="flex items-center justify-center gap-3">
          <span
            className="text-3xl font-bold text-[#ED2E24] font-mono tracking-wider"
            data-testid="customer-number"
          >
            {customerNumber}
          </span>
          <button
            onClick={copy}
            className="text-gray-400 hover:text-[#ED2E24] transition-colors"
            title="Copy Customer ID"
            data-testid="copy-customer-id-btn"
          >
            <Copy size={18} />
          </button>
        </div>
        <p className="text-xs text-gray-400 mt-3">
          Save this ID — you'll need it to track packages and access your KYo account.
        </p>
      </div>

      <button
        onClick={onBack}
        className="bg-[#ED2E24] hover:bg-[#c9231a] text-white font-semibold px-8 py-3 rounded-full transition-colors text-sm"
        data-testid="back-to-home-btn"
      >
        Go to KYo Group
      </button>
    </div>
  );
}

function VerificationSuccessScreen({ onBack }) {
  return (
    <div className="text-center py-4" data-testid="kyoown-verification-screen">
      <div className="flex justify-center mb-5">
        <div className="w-20 h-20 rounded-full bg-green-50 flex items-center justify-center">
          <CheckCircle size={48} className="text-green-500" />
        </div>
      </div>
      <h2 className="text-2xl font-bold text-gray-900 mb-2">Account Created!</h2>
      <p className="text-gray-600 text-sm mb-8 leading-relaxed max-w-sm mx-auto">
        Account created — please check your email to verify your account before signing in.
      </p>
      <button
        onClick={onBack}
        className="bg-[#ED2E24] hover:bg-[#c9231a] text-white font-semibold px-8 py-3 rounded-full transition-colors text-sm"
        data-testid="kyoown-back-to-home-btn"
      >
        Go to KYo Group
      </button>
    </div>
  );
}

/* ══════════════════════════════════════════════════════
   Main signup page
══════════════════════════════════════════════════════ */
export default function SignupPage() {
  const navigate = useNavigate();
  const [tab, setTab] = useState("personal");
  const [success, setSuccess] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [emailError, setEmailError] = useState(null);

  /* DOM refs for address inputs — used at submit time as fallback when React
     state hasn't synced yet (async batching, autocomplete interaction, paste) */
  const personalAddressRef = useRef(null);

  /* Personal form */
  const [p, setP] = useState({
    name: "", email: "", phone: "", address: "", country: "Jamaica",
    password: "", confirmPassword: "", trn: "",
  });

  /* Business form */
  const [b, setB] = useState({
    company_name: "", admin_name: "", company_phone: "", email: "",
    password: "", confirmPassword: "", plan: "Starter",
  });

  /* Clears all errors on every field edit — errors only appear after a submission attempt */
  const upP = (field) => (e) => { setError(null); setEmailError(null); setP((prev) => ({ ...prev, [field]: e.target.value })); };
  const upB = (field) => (e) => { setError(null); setB((prev) => ({ ...prev, [field]: e.target.value })); };

  /* Address autocomplete handlers — receive a string value, not a DOM event */
  const setPersonalAddress = (val) => { setError(null); setEmailError(null); setP((prev) => ({ ...prev, address: val })); };
  const setBusinessAddress = (val) => { setError(null); setB((prev) => ({ ...prev, company_address: val })); };

  /* Personal submit */
  const handlePersonalSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setEmailError(null);

    /* Strip dashes at submit time — valid regardless of how p.trn was populated */
    const trnDigits = p.trn.replace(/\D/g, "");
    const trnRequired = p.country === "Jamaica";

    /* Read address from React state; fall back to raw DOM value in case async
       state update hasn't landed yet (e.g. user clicked Submit immediately after
       typing without triggering another onChange cycle) */
    const address = p.address || personalAddressRef.current?.value?.trim() || "";

    if (!p.name || !p.email || !address || !p.country || !p.password || (trnRequired && !trnDigits)) {
      setError("Please fill in all required fields."); return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(p.email)) {
      setError("Please enter a valid email address."); return;
    }
    if (p.password !== p.confirmPassword) {
      setError("Passwords do not match."); return;
    }
    if (p.password.length < 6) {
      setError("Password must be at least 6 characters."); return;
    }
    if (trnRequired && trnDigits.length !== 9) {
      setError("TRN must be exactly 9 digits (e.g. 123-456-789)."); return;
    }

    setLoading(true);
    try {
      const payload = {
        name: p.name, email: p.email, address,
        country: COUNTRY_ISO[p.country] || p.country,
        password: p.password,
        ...(trnRequired && trnDigits && { trn: trnDigits }),
        ...(p.phone && { phone: p.phone }),
      };
      console.log("[KYo] Personal registration payload:", payload);
      const { data } = await axios.post(`${API}/register/personal`, payload);
      const cn = data.customer_number ?? data.customerNumber ?? data.id ?? data.customer_id ?? String(data);
      localStorage.setItem("kyo_customer_number", cn);
      setSuccess(cn);
    } catch (err) {
      console.log("[KYo] Registration API error:", err?.response?.status, err?.response?.data);
      const status = err?.response?.status;
      if (status === 500) {
        setEmailError("Registration failed. This email may already be registered — please try signing in instead.");
      } else if (isDuplicateEmail(err)) {
        setEmailError("An account with this email already exists. Please sign in or use a different email.");
      } else {
        setError(getApiError(err));
      }
    } finally {
      setLoading(false);
    }
  };

  /* Business submit */
  const handleBusinessSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    if (!b.company_name || !b.admin_name || !b.email || !b.password) {
      setError("Please fill in all required fields."); return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(b.email)) {
      setError("Please enter a valid email address."); return;
    }
    if (b.password !== b.confirmPassword) {
      setError("Passwords do not match."); return;
    }
    if (b.password.length < 6) {
      setError("Password must be at least 6 characters."); return;
    }

    setLoading(true);
    try {
      const payload = {
        company_name:   b.company_name,
        admin_name:     b.admin_name,
        admin_email:    b.email,
        admin_password: b.password,
        ...(b.company_phone && { phone: b.company_phone }),
      };
      console.log("[KYo] Tenant registration payload:", payload);
      const { data } = await axios.post(`${API}/register/business`, payload);
      console.log("[KYo] Tenant signup response:", JSON.stringify(data, null, 2));

      /* Redirect to WiPay / customer signup URL if present */
      const signupUrl = data.customer_signup_url || null;
      if (signupUrl) {
        window.location.href = signupUrl;
        return;
      }

      /* No redirect URL — show appropriate success screen */
      setSuccess(data.verification_required ? "VERIFICATION_REQUIRED" : (data.company_id || "ok"));
    } catch (err) {
      console.log("[KYo] Tenant registration error:", err?.response?.status, err?.response?.data);
      const status = err?.response?.status;
      if (status === 409) {
        setError("An account with this email already exists. Please sign in instead.");
      } else if (status === 422) {
        setError(err?.response?.data?.detail || "Invalid submission. Please check your details and try again.");
      } else {
        setError(getApiError(err));
      }
    } finally {
      setLoading(false);
    }
  };

  const switchTab = (next) => { setTab(next); setError(null); setEmailError(null); };

  return (
    <div className="min-h-screen bg-[#f2f2f2] flex flex-col" data-testid="signup-page">
      {/* Top bar */}
      <div className="pt-5 pb-2 px-6">
        <button
          onClick={() => navigate("/")}
          className="flex items-center gap-2 text-sm text-gray-500 hover:text-gray-800 transition-colors"
          data-testid="back-to-home-link"
        >
          <ArrowLeft size={16} />
          Back to KYo Group
        </button>
      </div>

      {/* Centered card */}
      <div className="flex-1 flex items-center justify-center px-4 py-8">
        <div
          className="bg-white rounded-2xl shadow-lg w-full max-w-[580px] p-8 sm:p-10"
          data-testid="signup-card"
        >
          {success === "VERIFICATION_REQUIRED" ? (
            <VerificationSuccessScreen onBack={() => navigate("/")} />
          ) : success !== null ? (
            <SuccessScreen customerNumber={success} onBack={() => navigate("/")} />
          ) : (
            <>
              {/* Heading */}
              <div className="text-center mb-6">
                <h1 className="text-3xl font-bold text-gray-900">Create your account</h1>
                <p className="text-gray-400 text-sm mt-1.5">Join KYo Group and start shipping today</p>
              </div>

              {/* Tab switcher */}
              <div
                className="flex bg-gray-100 rounded-full p-1 mb-6"
                data-testid="signup-tab-switcher"
              >
                <button
                  onClick={() => switchTab("personal")}
                  className={`flex-1 py-2 px-4 rounded-full text-sm font-medium transition-all duration-200 ${
                    tab === "personal"
                      ? "bg-[#ED2E24] text-white shadow"
                      : "text-gray-500 hover:text-gray-700"
                  }`}
                  data-testid="tab-personal"
                >
                  Aero
                </button>
                <button
                  onClick={() => switchTab("business")}
                  className={`flex-1 py-2 px-4 rounded-full text-sm font-medium transition-all duration-200 flex items-center justify-center gap-1.5 ${
                    tab === "business"
                      ? "bg-[#ED2E24] text-white shadow"
                      : "text-gray-500 hover:text-gray-700"
                  }`}
                  data-testid="tab-business"
                >
                  KYoown <span className="text-[#ED2E24]">👑</span>
                </button>
              </div>

              {/* Error banner */}
              {error && (
                <div
                  className="mb-5 bg-red-50 border border-red-200 text-red-700 text-sm px-4 py-3 rounded-xl flex items-start gap-2"
                  data-testid="signup-error"
                >
                  <Info size={15} className="mt-0.5 shrink-0" />
                  <span>{error}</span>
                </div>
              )}

              {/* ─── PERSONAL FORM ─── */}
              {tab === "personal" && (
                <form onSubmit={handlePersonalSubmit} noValidate data-testid="personal-signup-form">
                  <div className="bg-[#ED2E24] text-white text-center py-2.5 rounded-full font-semibold text-sm mb-6">
                    Aero sign up
                  </div>

                  <div className="space-y-5">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                      <FloatInput
                        label="Full Name" id="p-name" required
                        value={p.name} onChange={upP("name")}
                        placeholder="Enter your full name"
                      />
                      <div>
                        <FloatInput
                          label="Email" id="p-email" type="email"
                          value={p.email}
                          onChange={(e) => { upP("email")(e); }}
                          placeholder="info@xyz.com"
                          required
                        />
                        {emailError && (
                          <p className="mt-1.5 text-xs text-red-600 flex items-start gap-1" data-testid="email-inline-error">
                            <Info size={12} className="mt-0.5 shrink-0" />
                            {emailError}
                          </p>
                        )}
                      </div>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                      <FloatInput
                        label="Phone (Optional)" id="p-phone"
                        value={p.phone} onChange={upP("phone")}
                        placeholder="xxxxxxxxxx"
                      />
                      <FloatSelect
                        label="Country" id="p-country" required
                        value={p.country}
                        onChange={(e) => {
                          const v = e.target.value;
                          setP((prev) => ({ ...prev, country: v, ...(v !== "Jamaica" && { trn: "" }) }));
                        }}
                        options={COUNTRIES}
                      />
                    </div>

                    <AddressAutocomplete
                      ref={personalAddressRef}
                      label="Address" id="p-address" required
                      value={p.address} onChange={setPersonalAddress}
                      placeholder="Start typing your address…"
                    />

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                      <PasswordInput
                        label="Choose A Password (6 Chars)" id="p-password" required
                        value={p.password} onChange={upP("password")}
                      />
                      <PasswordInput
                        label="Confirm Password" id="p-confirm" required
                        value={p.confirmPassword} onChange={upP("confirmPassword")}
                      />
                    </div>

                    {p.country === "Jamaica" && (
                    <div>
                      {/* TRN — auto-formats to 123-456-789 as user types */}
                      <div className="relative">
                        <label
                          htmlFor="p-trn"
                          className="absolute -top-2.5 left-3 z-10 bg-white px-1 text-xs text-gray-500 leading-none select-none"
                        >
                          TRN#<span className="text-[#ED2E24] ml-0.5">*</span>
                        </label>
                        <input
                          id="p-trn"
                          type="text"
                          inputMode="numeric"
                          value={formatTrn(p.trn)}
                          onChange={(e) => {
                            const raw = e.target.value.replace(/\D/g, "").slice(0, 9);
                            setError(null);
                            setEmailError(null);
                            setP((prev) => ({ ...prev, trn: raw }));
                          }}
                          placeholder="123-456-789"
                          maxLength={11}
                          className="w-full border border-gray-300 rounded-xl px-3.5 pt-4 pb-3 text-sm text-gray-900 bg-white outline-none focus:border-gray-500 transition-colors placeholder:text-gray-300"
                          data-testid="trn-input"
                        />
                      </div>
                      <p className="mt-2 text-xs text-gray-400 flex items-start gap-1.5">
                        <Info size={12} className="mt-0.5 shrink-0" />
                        Format: 123-456-789. Required by Jamaican Customs for parcel clearance. Your TRN# is encrypted and masked on package documents.
                      </p>
                    </div>
                    )}
                  </div>

                  <button
                    type="submit"
                    disabled={loading}
                    className="mt-7 w-full bg-[#ED2E24] hover:bg-[#c9231a] disabled:opacity-70 text-white font-semibold py-3.5 rounded-full transition-colors text-sm"
                    data-testid="personal-submit-btn"
                  >
                    {loading ? "Creating account…" : "Sign up with KYo"}
                  </button>
                  <p className="text-center mt-3 text-[11px] text-gray-400 leading-relaxed">
                    By signing up, you agree to our{" "}
                    <a href="/terms" target="_blank" rel="noreferrer" className="text-[#ED2E24] hover:underline">Terms &amp; Conditions</a>
                    {" "}and{" "}
                    <a href="/privacy-policy" target="_blank" rel="noreferrer" className="text-[#ED2E24] hover:underline">Privacy Policy</a>.
                  </p>
                </form>
              )}

              {/* ─── BUSINESS / KYoown FORM ─── */}
              {tab === "business" && (
                <form onSubmit={handleBusinessSubmit} noValidate data-testid="business-signup-form">
                  <div className="bg-[#ED2E24] text-white text-center py-2.5 rounded-full font-semibold text-sm mb-4">
                    Courier sign up
                  </div>

                  {/* Description */}
                  <div className="mb-6 text-center">
                    <p className="font-semibold text-gray-800 text-sm">
                      Start running your courier business on KYoown in minutes.
                    </p>
                    <p className="text-gray-500 text-xs mt-1.5 leading-relaxed">
                      Create your tenant workspace, get a permanent public customer signup URL, and start managing packages today.
                    </p>
                    <ul className="mt-3 space-y-1 text-left inline-block">
                      {[
                        "Multi-role team & RBAC",
                        "WiPay-powered invoicing",
                        "Auto driver assignment",
                        "Air / sea / ground manifests",
                        "Branded PDFs & emails",
                      ].map((item) => (
                        <li key={item} className="text-xs text-gray-500 flex items-center gap-2">
                          <span className="text-[#ED2E24] font-bold text-base leading-none">·</span>
                          {item}
                        </li>
                      ))}
                    </ul>
                  </div>

                  <div className="space-y-5">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                      <FloatInput
                        label="Company Name" id="b-company-name" required
                        value={b.company_name} onChange={upB("company_name")}
                        placeholder="Enter company name"
                      />
                      <FloatInput
                        label="Admin Name" id="b-admin-name" required
                        value={b.admin_name} onChange={upB("admin_name")}
                        placeholder="Your full name"
                      />
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                      <FloatInput
                        label="Email" id="b-email" type="email"
                        value={b.email} onChange={upB("email")}
                        placeholder="info@xyz.com"
                        required
                      />
                      <FloatInput
                        label="Phone (Optional)" id="b-company-phone"
                        value={b.company_phone} onChange={upB("company_phone")}
                        placeholder="xxxxxxxxxx"
                      />
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                      <PasswordInput
                        label="Choose A Password (6 Chars)" id="b-password" required
                        value={b.password} onChange={upB("password")}
                      />
                      <PasswordInput
                        label="Confirm Password" id="b-confirm" required
                        value={b.confirmPassword} onChange={upB("confirmPassword")}
                      />
                    </div>

                    {/* Plan — read-only, hardcoded to Starter on the backend */}
                    <div className="relative">
                      <label className="absolute -top-2.5 left-3 z-10 bg-white px-1 text-xs text-gray-500 leading-none select-none">
                        Plan
                      </label>
                      <div className="w-full border border-gray-200 bg-gray-50 rounded-xl px-3.5 pt-4 pb-3 text-sm text-gray-700 flex items-center justify-between cursor-not-allowed">
                        <span>Starter</span>
                        <span className="text-[11px] font-semibold text-gray-400 bg-gray-200 rounded-full px-2 py-0.5 tracking-wide">JM</span>
                      </div>
                    </div>
                  </div>

                  <button
                    type="submit"
                    disabled={loading}
                    className="mt-7 w-full bg-[#ED2E24] hover:bg-[#c9231a] disabled:opacity-70 text-white font-semibold py-3.5 rounded-full transition-colors text-sm"
                    data-testid="business-submit-btn"
                  >
                    {loading ? "Processing…" : "Pay & Sign Up"}
                  </button>
                  <p className="text-center mt-3 text-[11px] text-gray-400 leading-relaxed">
                    By signing up, you agree to our{" "}
                    <a href="/terms" target="_blank" rel="noreferrer" className="text-[#ED2E24] hover:underline">Terms &amp; Conditions</a>
                    {" "}and{" "}
                    <a href="/privacy-policy" target="_blank" rel="noreferrer" className="text-[#ED2E24] hover:underline">Privacy Policy</a>.
                  </p>
                </form>
              )}

              {/* Sign in link */}
              <p className="text-center mt-5 text-sm text-gray-500">
                Already have account?{" "}
                <button
                  onClick={() => navigate("/signin")}
                  className="text-[#ED2E24] hover:underline font-medium"
                  data-testid="signin-link"
                >
                  Sign in
                </button>
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
